import { useEffect } from 'react';

const SITE_URL = 'https://get-goods.ai';

type JsonLd = Record<string, unknown> | Record<string, unknown>[];

interface SEOProps {
  title: string;
  description: string;
  canonicalPath?: string;
  imagePath?: string;
  keywords?: string;
  type?: string;
  jsonLd?: JsonLd;
}

const toAbsoluteUrl = (path: string) => {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

const ensureMetaTag = (attribute: 'name' | 'property', key: string) => {
  let element = document.querySelector(`meta[${attribute}="${key}"]`) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  return element;
};

const ensureCanonicalTag = () => {
  let element = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  return element;
};

const SEO = ({
  title,
  description,
  canonicalPath = '/',
  imagePath = '/logo512.png',
  keywords,
  type = 'website',
  jsonLd,
}: SEOProps) => {
  useEffect(() => {
    const canonicalUrl = toAbsoluteUrl(canonicalPath);
    const imageUrl = toAbsoluteUrl(imagePath);

    document.documentElement.lang = 'ru';
    document.title = title;

    ensureMetaTag('name', 'description').content = description;
    ensureMetaTag('name', 'robots').content = 'index, follow';
    ensureMetaTag('name', 'theme-color').content = '#2563eb';

    if (keywords) {
      ensureMetaTag('name', 'keywords').content = keywords;
    }

    ensureMetaTag('property', 'og:title').content = title;
    ensureMetaTag('property', 'og:description').content = description;
    ensureMetaTag('property', 'og:type').content = type;
    ensureMetaTag('property', 'og:url').content = canonicalUrl;
    ensureMetaTag('property', 'og:image').content = imageUrl;
    ensureMetaTag('property', 'og:site_name').content = 'Get-Goods.ai';
    ensureMetaTag('property', 'og:locale').content = 'ru_RU';

    ensureMetaTag('name', 'twitter:card').content = 'summary_large_image';
    ensureMetaTag('name', 'twitter:title').content = title;
    ensureMetaTag('name', 'twitter:description').content = description;
    ensureMetaTag('name', 'twitter:image').content = imageUrl;

    ensureCanonicalTag().href = canonicalUrl;

    document.querySelectorAll('script[data-seo-json-ld="true"]').forEach((script) => script.remove());

    if (jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.seoJsonLd = 'true';
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, canonicalPath, imagePath, keywords, type, jsonLd]);

  return null;
};

export default SEO;
